/**
 * RF Cart Frequency
 *
 * Web component que renderiza um <select> com as opções do offer_group do kit
 * atual. Ao trocar, adiciona o novo kit via RafobaseCart.addOffer e zera as
 * linhas do kit atual via /cart/update.js.
 *
 * Data attributes:
 *   data-current-slug     - slug do kit atualmente no carrinho
 *   data-current-indexes  - índices (1-based) das line items a serem removidas
 *
 * Depende de <script id="rf-rafobase-data" type="application/json"> no DOM.
 */

class RfCartFrequency extends HTMLElement {
  constructor() {
    super();
    this._busy = false;
    this._data = null;
    this._errorTimer = null;
  }

  connectedCallback() {
    this._data = this._loadData();
    if (!this._data) {
      // Sem dados, mantém fallback (span com texto estático)
      return;
    }

    const currentSlug = this.dataset.currentSlug;
    if (!currentSlug) return;

    const group = this._findOfferGroup(currentSlug);
    if (!group) {
      // Kit não pertence a nenhum offer_group conhecido — mantém fallback
      return;
    }

    this._renderSelect(group, currentSlug);
  }

  disconnectedCallback() {
    clearTimeout(this._errorTimer);
  }

  _loadData() {
    const el = document.getElementById('rf-rafobase-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('[rf-cart-frequency] JSON inválido em #rf-rafobase-data', err);
      return null;
    }
  }

  _findOfferGroup(kitSlug) {
    const groups = this._data.offer_groups || {};
    for (const key in groups) {
      const g = groups[key];
      const kits = Array.isArray(g.kits) ? g.kits : [];
      if (kits.some((k) => k && k.kit === kitSlug)) return g;
    }
    return null;
  }

  _renderSelect(group, currentSlug) {
    const kitsMeta = this._data.product_kits || {};
    const entries = Array.isArray(group.kits) ? group.kits : [];

    const options = entries
      .map((entry) => {
        const meta = kitsMeta[entry.kit];
        if (!meta) return null;
        return {
          value: entry.kit,
          label: this._formatLabel(meta, entry),
          selected: entry.kit === currentSlug,
        };
      })
      .filter(Boolean);

    if (options.length < 2) {
      // Sem alternativas — nada pra escolher, mantém fallback
      return;
    }

    const select = document.createElement('select');
    select.className = 'rf-cart-frequency__select';
    select.setAttribute('aria-label', 'Alterar frequência da assinatura');

    for (const opt of options) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      if (opt.selected) o.selected = true;
      select.appendChild(o);
    }

    select.addEventListener('change', (e) => this._handleChange(e));

    // Substitui conteúdo (span de fallback) pelo select
    this.textContent = '';
    this.appendChild(select);
    this.setAttribute('data-hydrated', '');
  }

  _formatLabel(meta, entry) {
    const freq = (meta.frequency || '').trim();
    const price = this._formatPrice(meta.display_price);
    const freqLabel = this._freqLabel(freq);
    const suffix = this._freqSuffix(freq);

    if (freqLabel) {
      return `${freqLabel} — ${price}${suffix}`;
    }
    return `Compra única — ${price}`;
  }

  _formatPrice(value) {
    const num = typeof value === 'string' ? parseFloat(value) : Number(value) || 0;
    try {
      return num.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });
    } catch {
      return `R$ ${num.toFixed(2)}`;
    }
  }

  _freqLabel(f) {
    return (
      {
        M: 'Assinatura Mensal',
        T: 'Assinatura Trimestral',
        S: 'Assinatura Semestral',
        A: 'Assinatura Anual',
      }[f] || ''
    );
  }

  _freqSuffix(f) {
    return (
      {
        M: '/mês',
        T: '/trimestre',
        S: '/semestre',
        A: '/ano',
      }[f] || ''
    );
  }

  async _handleChange(e) {
    if (this._busy) return;

    const select = e.target;
    const newSlug = select.value;
    const currentSlug = this.dataset.currentSlug;

    if (!newSlug || newSlug === currentSlug) return;

    this._busy = true;
    this.setAttribute('data-switching', '');
    select.disabled = true;

    try {
      // Fase 1: remove kit atual via primitivo do Rafobase (atômico, escala brindes)
      if (window.RafobaseCart?.removeKit) {
        await window.RafobaseCart.removeKit(currentSlug);
      } else if (window.RafobaseCart?.setKitQty) {
        await window.RafobaseCart.setKitQty(currentSlug, 0);
      } else {
        throw new Error('RafobaseCart.removeKit/setKitQty indisponível');
      }

      // Fase 2: adiciona novo kit
      if (typeof window.RafobaseCart?.addOffer !== 'function') {
        throw new Error('RafobaseCart.addOffer indisponível');
      }
      await window.RafobaseCart.addOffer(newSlug);

      // Fase 3: reload pra rehidratar dropdown/dados
      window.location.reload();
    } catch (err) {
      console.error('[rf-cart-frequency] Falha ao trocar frequência:', err);
      select.value = currentSlug;
      select.disabled = false;
      this.removeAttribute('data-switching');
      this._busy = false;
      this._showError('Não foi possível trocar a frequência. Tente novamente.');
    }
  }

  _showError(message) {
    let errorEl = this.querySelector('[data-frequency-error]');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'rf-cart-frequency__error';
      errorEl.setAttribute('data-frequency-error', '');
      errorEl.setAttribute('role', 'alert');
      this.appendChild(errorEl);
    }
    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('rf-cart-frequency')) {
  customElements.define('rf-cart-frequency', RfCartFrequency);
}
